import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useIsAuthenticated } from "./auth";
import { isMaintenanceActive } from "@shared/maintenance";
import type { SiteSettingsData } from "@shared/schema";

declare global {
  interface Window {
    /** A szerver írja az index.html-be (lásd server/seo.ts). */
    __MAINTENANCE__?: boolean;
  }
}

/** A szerver által az első festéshez beégetett állapot (dev-ben hiányzik). */
function bootFlag(): boolean {
  return typeof window !== "undefined" && window.__MAINTENANCE__ === true;
}

/** Percenként „ketyeg", hogy az időzített ki-/bekapcsolás reload nélkül is bejöjjön. */
function useTick(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * Rejtve van-e MOST a publikus tartalom a látogató elől?
 *
 * - A bejelentkezett admin SOHA nem látja a „Hamarosan" oldalt — neki a valódi
 *   tartalom kell, hogy javítani tudja (az admin élő előnézet is ezen múlik).
 * - Az első festésnél a szerver beégetett jelzése dönt (nincs tartalom-villanás),
 *   utána a betöltött beállítás — így az időzítés is élőben érvényesül.
 */
export function useSiteHidden(): boolean {
  const isAdmin = useIsAuthenticated();
  const now = useTick();
  const q = useQuery({
    queryKey: ["site-settings"],
    queryFn: () =>
      api<SiteSettingsData>("/api/site-settings", { withAuth: false }),
    staleTime: 60_000,
  });
  if (isAdmin) return false;
  if (q.isSuccess) return isMaintenanceActive(q.data?.maintenance, now);
  return bootFlag();
}
